import { Info, LoaderCircle, RotateCcw, Save, ShieldCheck } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { toast } from "sonner";

import type { StrategySettings } from "../../shared/contracts";
import { DefinitionRow } from "../components/data-display";
import { DashboardLoading, ErrorState, PageHeader } from "../components/page-state";
import { Button } from "../components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../hooks/use-auth";
import { useDashboard } from "../hooks/use-dashboard";
import { api, getErrorMessage } from "../lib/api";
import { formatPercent, formatUsd } from "../lib/format";
import { applyStrategyPreset } from "../lib/strategy-presets";

const strategyKeys: Array<keyof StrategySettings> = [
  "preset",
  "amountUsd",
  "allocationPercent",
  "minimumNetEdgePercent",
  "grossTargetPercent",
  "stopDistancePercent",
  "maxHoldMinutes",
  "lossStreakLimit",
  "reinvestProfits",
  "slippageAllowancePercent",
];

function strategiesMatch(left: StrategySettings | null, right: StrategySettings | null) {
  return left !== null && right !== null && strategyKeys.every((key) => left[key] === right[key]);
}

function NumericField({
  id,
  label,
  description,
  value,
  min = 0,
  max,
  step = 0.1,
  suffix,
  disabled = false,
  onChange,
}: {
  id: string;
  label: string;
  description: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <Field data-disabled={disabled || undefined}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="relative">
        <Input
          id={id}
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(event) => {
            const next = event.currentTarget.valueAsNumber;
            if (Number.isFinite(next)) onChange(next);
          }}
          className={suffix ? "pr-16" : undefined}
          disabled={disabled}
          required
        />
        {suffix && <span className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-xs font-semibold text-muted-foreground">{suffix}</span>}
      </div>
      <FieldDescription>{description}</FieldDescription>
    </Field>
  );
}

export function StrategyPage() {
  const { session } = useAuth();
  const { dashboard, loading, error, refresh, replaceDashboard } = useDashboard();
  const [draft, setDraft] = useState<StrategySettings | null>(null);
  const [busy, setBusy] = useState(false);
  const settings = draft ?? dashboard?.strategy ?? null;
  const isDirty = useMemo(
    () => !strategiesMatch(settings, dashboard?.strategy ?? null),
    [dashboard?.strategy, settings],
  );

  const feeFloor = useMemo(() => {
    if (!dashboard || dashboard.fees.makerPercent == null || !settings) return null;
    return dashboard.fees.makerPercent * 2 + settings.slippageAllowancePercent;
  }, [dashboard, settings]);

  if (loading && !dashboard) return <DashboardLoading />;
  if (!dashboard || !settings) return <ErrorState message={error ?? "Strategy settings could not be loaded."} onRetry={() => void refresh()} />;
  const currentDashboard = dashboard;
  const baseStrategy = dashboard.strategy;
  const makerRoundTripPercent = dashboard.fees.makerPercent === null ? null : dashboard.fees.makerPercent * 2;
  const emergencyFeePercent = dashboard.fees.makerPercent === null || dashboard.fees.takerPercent === null
    ? null
    : dashboard.fees.makerPercent + dashboard.fees.takerPercent;
  const allocationCapUsd = dashboard.portfolio.usdAvailable === null
    ? null
    : dashboard.portfolio.usdAvailable * (settings.allocationPercent / 100);
  const plannedExposureUsd = allocationCapUsd === null
    ? settings.amountUsd
    : Math.min(settings.amountUsd, allocationCapUsd);

  function update<K extends keyof StrategySettings>(key: K, value: StrategySettings[K], markCustom = true) {
    setDraft((current) => {
      const base = current ?? baseStrategy;
      return { ...base, [key]: value, ...(markCustom && key !== "preset" ? { preset: "custom" as const } : {}) };
    });
  }

  function choosePreset(preset: StrategySettings["preset"]) {
    setDraft((current) => applyStrategyPreset(current ?? baseStrategy, preset));
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    const currentSettings = settings;
    if (!currentSettings || !isDirty) return;
    if (!session.csrfToken) {
      toast.error("Your secure session needs to be refreshed.");
      return;
    }
    if (currentSettings.stopDistancePercent >= currentSettings.grossTargetPercent) {
      toast.error("The protective stop must be smaller than the gross target.");
      return;
    }
    if (currentSettings.minimumNetEdgePercent <= 0 || currentSettings.amountUsd <= 0 || currentSettings.maxHoldMinutes <= 0) {
      toast.error("Trading amount, minimum edge, and maximum hold must be greater than zero.");
      return;
    }
    setBusy(true);
    try {
      const saved = await api.updateStrategy(currentSettings, session.csrfToken);
      replaceDashboard({ ...currentDashboard, strategy: saved });
      setDraft(null);
      toast.success("Strategy settings saved.");
    } catch (caught) {
      toast.error(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="grid gap-8 pb-24 md:pb-0" onSubmit={(event) => void save(event)}>
      <PageHeader
        eyebrow="ETH MOMENTUM-PULLBACK"
        title="Clear rules, editable safeguards."
        description="Tune the strategy without turning the dashboard into an indicator wall. Changes apply to future orders."
        actionsClassName="hidden md:flex"
        actions={
          <>
            <Button type="button" variant="outline" size="sm" onClick={() => setDraft(null)} disabled={busy || !isDirty}><RotateCcw data-icon="inline-start" /> Discard unsaved changes</Button>
            <Button type="submit" variant="signal" size="sm" disabled={busy || !isDirty}>{busy ? <LoaderCircle data-icon="inline-start" className="animate-spin" /> : <Save data-icon="inline-start" />} Save strategy</Button>
          </>
        }
      />

      <div
        className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm ${isDirty ? "border-primary/20 bg-card font-semibold" : "border-border bg-muted/45 text-muted-foreground"}`}
        role="status"
        aria-live="polite"
      >
        <span className={`size-2 rounded-full ${isDirty ? "bg-primary" : "bg-positive"}`} aria-hidden="true" />
        {isDirty ? "Unsaved strategy changes" : "Strategy matches the last saved settings"}
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm leading-6">
        <Info className="mt-1 size-4 shrink-0 text-muted-foreground" />
        <p><strong>No artificial daily trade cap.</strong> Trades are still governed by valid setups, available balance, Kraken rules, and the safety controls enabled here.</p>
      </div>

      <section className="grid gap-7 border-b border-border pb-8 lg:grid-cols-[minmax(0,1fr)_minmax(17rem,0.42fr)]" aria-labelledby="preset-heading">
        <div>
          <p className="text-xs font-bold text-muted-foreground">STRATEGY PROFILE</p>
          <h2 id="preset-heading" className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">ETH Momentum-Pullback</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">15-minute direction, 5-minute confirmation, and maker-first execution on ETH/USD spot. Choosing a preset updates every numeric control below for review before saving.</p>
          <div className="mt-5 grid max-w-xl grid-cols-2 gap-2 sm:grid-cols-4" role="group" aria-label="Strategy preset">
            {(["conservative", "standard", "aggressive", "custom"] as StrategySettings["preset"][]).map((preset) => (
              <button
                key={preset}
                type="button"
                className={`min-h-11 rounded-xl border px-3 text-sm font-semibold capitalize transition-colors ${settings.preset === preset ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:bg-muted"}`}
                aria-pressed={settings.preset === preset}
                onClick={() => choosePreset(preset)}
              >{preset}</button>
            ))}
          </div>
        </div>
        <dl className="rounded-2xl border border-border bg-card px-5 py-2">
          <DefinitionRow label="Pair" value="ETH/USD" />
          <DefinitionRow label="Planned exposure" value={formatUsd(plannedExposureUsd)} emphasis />
          <DefinitionRow label="Maker round trip" value={formatPercent(makerRoundTripPercent)} />
          <DefinitionRow label="Maker + taker fees" value={formatPercent(emergencyFeePercent)} />
          <DefinitionRow label="Entry" value="Post-only maker" />
          <DefinitionRow label="Emergency exit" value="Market allowed" />
        </dl>
      </section>

      <section className="grid gap-8 xl:grid-cols-2" aria-labelledby="capital-heading">
        <div>
          <p className="text-xs font-bold text-muted-foreground">CAPITAL & EDGE</p>
          <h2 id="capital-heading" className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">Position economics</h2>
          <FieldGroup className="mt-5 sm:grid-cols-2">
            <NumericField id="amount-usd" label="Amount per trade" description={`Available USD: ${formatUsd(dashboard.portfolio.usdAvailable)}`} value={settings.amountUsd} step={1} suffix="USD" onChange={(value) => update("amountUsd", value)} />
            <NumericField id="allocation" label="Maximum allocation" description="Share of available portfolio balance." value={settings.allocationPercent} max={100} suffix="%" onChange={(value) => update("allocationPercent", value)} />
            <NumericField id="net-edge" label="Minimum estimated net return" description="Required after fees and slippage." value={settings.minimumNetEdgePercent} suffix="%" onChange={(value) => update("minimumNetEdgePercent", value)} />
            <NumericField id="slippage" label="Slippage allowance" description="Conservative execution estimate." value={settings.slippageAllowancePercent} suffix="%" step={0.01} onChange={(value) => update("slippageAllowancePercent", value)} />
          </FieldGroup>
          <div className="mt-5 rounded-xl bg-muted px-4 py-3 text-xs leading-5 text-muted-foreground">
            {feeFloor == null
              ? "Authenticated Kraken fees are unavailable. Live entries should remain disabled until the fee check succeeds."
              : `Current maker/maker fees plus your slippage allowance consume approximately ${formatPercent(feeFloor)} before profit. This is an estimate, not a guarantee.`}
          </div>
        </div>

        <div>
          <p className="text-xs font-bold text-muted-foreground">EXIT PLAN</p>
          <h2 className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">Target, stop, and time</h2>
          <FieldGroup className="mt-5 sm:grid-cols-2">
            <NumericField id="gross-target" label="Gross profit target" description="Before fees and slippage." value={settings.grossTargetPercent} suffix="%" onChange={(value) => update("grossTargetPercent", value)} />
            <NumericField id="stop-distance" label="Protective stop distance" description="May execute with taker fees and slippage." value={settings.stopDistancePercent} suffix="%" onChange={(value) => update("stopDistancePercent", value)} />
            <NumericField id="max-hold" label="Maximum hold time" description="Close or reassess when time expires." value={settings.maxHoldMinutes} step={1} suffix="MIN" onChange={(value) => update("maxHoldMinutes", value)} />
            <NumericField id="loss-streak" label="Loss-streak pause" description={settings.lossStreakLimit === null ? "Enable loss-streak protection below to edit this limit." : "Set how many consecutive losses trigger a pause."} value={settings.lossStreakLimit ?? 2} step={1} min={1} suffix="LOSSES" disabled={settings.lossStreakLimit === null} onChange={(value) => update("lossStreakLimit", value)} />
          </FieldGroup>
        </div>
      </section>

      <section className="grid gap-4 border-t border-border pt-7" aria-labelledby="automation-heading">
        <div>
          <p className="text-xs font-bold text-muted-foreground">AUTOMATION</p>
          <h2 id="automation-heading" className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">Optional behavior</h2>
        </div>
        <div className="flex items-center justify-between gap-5 rounded-xl border border-border bg-card px-4 py-3 sm:px-5">
          <div>
            <label htmlFor="reinvest-profits" className="block cursor-pointer text-sm font-semibold">Reinvest realized profits</label>
            <p id="reinvest-profits-description" className="mt-1 text-xs leading-5 text-muted-foreground">Allow future position sizes to use realized gains, within the allocation limit.</p>
          </div>
          <Switch id="reinvest-profits" checked={settings.reinvestProfits} onCheckedChange={(checked) => update("reinvestProfits", checked)} aria-describedby="reinvest-profits-description" />
        </div>
        <div className="flex items-center justify-between gap-5 rounded-xl border border-border bg-card px-4 py-3 sm:px-5">
          <div>
            <label htmlFor="loss-streak-protection" className="block cursor-pointer text-sm font-semibold">Pause after a loss streak</label>
            <p id="loss-streak-protection-description" className="mt-1 text-xs leading-5 text-muted-foreground">A safety pause, not a daily trade count limit.</p>
          </div>
          <Switch id="loss-streak-protection" checked={settings.lossStreakLimit !== null} onCheckedChange={(checked) => update("lossStreakLimit", checked ? 2 : null)} aria-describedby="loss-streak-protection-description" />
        </div>
        <div className="flex items-start gap-3 rounded-xl bg-accent px-4 py-3 text-sm leading-6 text-accent-foreground">
          <ShieldCheck className="mt-1 size-4 shrink-0" />
          <p>Live orders still require a current Kraken connection, authenticated fee data, fresh market data, and a complete signal pass.</p>
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-20 border-t border-border bg-card/97 px-3 py-2 shadow-[0_-12px_32px_rgba(13,29,38,0.12)] backdrop-blur-sm md:hidden">
        <div className="mx-auto flex max-w-lg items-center justify-between gap-3">
          <p className="min-w-0 text-xs font-semibold" aria-live="polite">
            {isDirty ? "Unsaved changes" : "All changes saved"}
          </p>
          <div className="flex shrink-0 items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setDraft(null)} disabled={busy || !isDirty} aria-label="Discard unsaved strategy changes">
              <RotateCcw aria-hidden="true" />
              Revert
            </Button>
            <Button type="submit" variant="signal" size="sm" disabled={busy || !isDirty}>
              {busy ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : <Save aria-hidden="true" />}
              Save
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
